import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { Col } from 'reaxl';
import { InventoryListingPlaceholder } from 'reaxl-listing';

import getVehicleHighlightWithFallback from '@/utilities/getVehicleHighlightWithFallback';

import cpoContentDuck from '@/ducks/cpoContentDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpPrimeSpotlightDuck from '@/ducks/srp/srpPrimeSpotlightDuck.js';
import userDuck from '@/ducks/userDuck.js';

import InventoryBoostContainer from './InventoryBoostContainer';
import InventorySpotlightContainer from './InventorySpotlightContainer';
import PremiumSpotlightContainer from './PremiumSpotlightContainer';

/**
 * render a special listing that is not part of the base listings call
 *
 * @param listingIndex index at which the spotlight should be added
 * @param listingCategory type of special listing
 * @returns {*} the markup with spotlight(s) being injected in the results
 */

function InventorySpecialListing({
    listingId,
    listingIndex,
    listingCategory = 'spotlight',
}) {
    const { brand } = useBrand();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const isFiltersVisible = useSelector(srpFiltersDuck.selectors.getFiltersVisible);
    const userLocation = useSelector(userDuck.selectors.getCityState);
    const vehicleHighlightListings = useSelector(srpPrimeSpotlightDuck.selectors.getActiveInventory, _isEqual);
    const certifiedSponsor = useSelector(cpoContentDuck.selectors.selectCertifiedContentSimple);

    const {
        vehicleHighlightListingsFinal: vehicleHighlightCarouselListings = [],
    } = getVehicleHighlightWithFallback(vehicleHighlightListings);
    // use first vehicle highlight listing for boo listing
    const vehicleHighlightBooListing = vehicleHighlightCarouselListings.length > 0
        ? vehicleHighlightCarouselListings[0]
        : null;
    const currentPage = useSelector(srpPaginationDuck.selectors.getCurrentPage);

    const {
        brand: [, { base_url: baseUrl }],
        override_carfax_tile: [isOverrideCarfaxTileEnabled, { partner }],
        premium_spotlight: [enablePremiumSpotlight],
        spotlight: [isSpotlightEnabled], // feature config
        schema: [, { listing_type: schemaType, vehicle_feature_description: featureDescription, vehicle_price_description: priceDescription }],
        remove_carfax_tile: [removeCarfaxTile],
        remove_experian_tile: [removeExperianTile],
        vehicle_highlight: [isVehicleHighlightEnabled],
    } = useFeatures([
        'brand',
        'override_carfax_tile',
        'premium_spotlight',
        'spotlight',
        'schema',
        'remove_carfax_tile',
        'remove_experian_tile',
        'vehicle_highlight',
    ]);

    const schemaTemplate = useMemo(() => ({
        featureDescription,
        priceDescription,
    }), [featureDescription, priceDescription]);

    const listingProps = {
        baseUrl,
        schemaTemplate,
        schemaType,
        userLocation,
        currentPage,
        listingIndex,
        listingId,
        brand,
        certifiedSponsor,
        isOverrideCarfaxTileEnabled,
        partner,
        removeCarfaxTile,
        removeExperianTile,
    };

    const enableVehicleHighlights = enablePremiumSpotlight && isVehicleHighlightEnabled;

    if (isFiltersVisible && isXs) {
        return (
            <Col
                xs={12}
                sm={6}
                md={4}
                key={`${listingCategory}-placeholder-${listingIndex}`}
            >
                <InventoryListingPlaceholder
                    data-cmp="inv-placeholder-lstg"
                />
            </Col>
        );
    }

    if (listingCategory === 'spotlight' && !!isSpotlightEnabled) {
        return (
            <InventorySpotlightContainer
                key={`spotlight-${listingIndex}`}
                {...listingProps}
            />
        );
    }

    if (listingCategory === 'boost') {
        return (
            <InventoryBoostContainer
                key={`boost-${listingIndex}`}
                {...listingProps}
            />
        );
    }

    if (enableVehicleHighlights && listingCategory === 'peekaboo' && vehicleHighlightBooListing) {
        return (
            <PremiumSpotlightContainer
                premiumSpotlight={vehicleHighlightBooListing}
            />
        );
    }

    return null;
}

export default InventorySpecialListing;

InventorySpecialListing.propTypes = {
    listingId: PropTypes.string,
    listingIndex: PropTypes.number.isRequired,
    listingCategory: PropTypes.oneOf(['spotlight', 'boost', 'peekaboo']),
};
